"use client";
import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const contactEmail = process.env.NEXT_PUBLIC_APP_EMAIL;
  const appPhone = process.env.NEXT_PUBLIC_APP_PHONE;
  const appLocation = process.env.NEXT_PUBLIC_APP_LOCATION;


  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: "" });
    }
    if (error) setError(null);
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};
    
    if (!form.name.trim()) {
      errors.name = "Name is required";
    } else if (form.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }
    
    if (!form.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(form.email.trim())) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!form.message.trim()) {
      errors.message = "Message is required";
    } else if (form.message.trim().length < 10) {
      errors.message = "Message must be at least 10 characters";
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(null);
    setError(null);
    setFieldErrors({});
    
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    
    try {
      // Dynamic import to avoid SSR issues
      const { apiFetch } = await import("@/utils/api");
      const res = await apiFetch<{ success: boolean; message: string; data?: unknown }>("/contact", {
        method: "POST",
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim()
        }),
      });
      setSuccess(res.message || "Thank you for your message! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", message: "" });
      setFieldErrors({});
    } catch (err) {
      setError((err as Error).message || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-primary-50 via-white to-purple-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-600 to-purple-700 text-white">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl md:text-5xl lg:text-5xl font-bold lg:mb-4 mb-2 animate-fade-in">
              Get in Touch
            </h1>
            <p className="text-md md:text-2xl text-primary-100 max-w-3xl mx-auto">
              We&apos;d love to hear from you. Send us a message and we&apos;ll respond as soon as possible.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10 sm:py-16 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info (Mobile: appears first, Desktop: appears left) */}
          <div className="space-y-8">
            <div className='lg:block hidden'> 
              <h2 className="text-3xl font-bold text-gray-900 mb-1">Contact Information</h2>
              <p className="text-lg text-gray-600 mb-8">
                Have questions about our coupons and deals? We&apos;re here to help!
              </p>
            </div>
            
            <div className='lg:hidden block'> {/* Mobile: Smaller*/}
              <h2 className="text-2xl font-bold text-gray-900 text-center">Contact Information</h2>
              <p className="text-base text-gray-600 mb-6 text-center">
                Have questions? Here&apos;s how to reach us:
              </p>
            </div>

            {/* ENHANCED CONTACT BLOCKS*/}
            <div className="space-y-4 lg:space-y-6 sm:space-y-6">
              
              <a href={`mailto:${contactEmail}`} className="flex items-center space-x-4 p-4 lg:p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg hover:ring-2 hover:ring-primary-200 duration-300 w-full">
                <div className="flex-shrink-0 p-3 lg:p-4 rounded-full bg-primary-100">
                  <Mail className="h-5 w-5 lg:h-6 lg:w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base lg:text-lg font-semibold text-gray-900">Email</h3>
                  <p className="text-sm lg:text-base text-gray-600 break-all">{contactEmail}</p>
                </div>
              </a>
              
              {/* Phone Block */}
              {appPhone && (
              <a href={`tel:${appPhone}`} className="flex items-center space-x-4 p-4 lg:p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg hover:ring-2 hover:ring-primary-200 duration-300 w-full">
                <div className="flex-shrink-0 p-3 lg:p-4 rounded-full bg-primary-100">
                  <Phone className="h-5 w-5 lg:h-6 lg:w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base lg:text-lg font-semibold text-gray-900">Phone</h3>
                  <p className="text-sm lg:text-base text-gray-600">{appPhone}</p>
                </div>
              </a>
              )}
              
              {/* Address Block - Anchor tag removed, styled as div */}
              { appLocation && (
              <div className="flex items-center space-x-4 p-4 lg:p-6 bg-white rounded-xl shadow-md transition-shadow hover:shadow-lg hover:ring-2 hover:ring-primary-200 duration-300 w-full">
                <div className="flex-shrink-0 p-3 lg:p-4 rounded-full bg-primary-100">
                  <MapPin className="h-5 w-5 lg:h-6 lg:w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base lg:text-lg font-semibold text-gray-900">Address</h3>
                  <p className="text-sm lg:text-base text-gray-600">{appLocation}</p>
                </div>
              </div>
              )}
            </div>
          </div>
          

          {/* Contact Form (Mobile: appears second, Desktop: appears right) */}
          <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
            
            {success && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-3 animate-fade-in">
                <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                <p className="text-green-800 font-medium">{success}</p>
              </div>
            )}
            
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-3 animate-fade-in">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('name')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                    fieldErrors.name 
                      ? 'border-red-500 bg-red-50' 
                      : focusedField === 'name' 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="Enter your full name"
                  required
                  aria-invalid={!!fieldErrors.name}
                  aria-describedby={fieldErrors.name ? "name-error" : undefined}
                />
                {fieldErrors.name && (
                  <p id="name-error" className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{fieldErrors.name}</span>
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ${
                    fieldErrors.email 
                      ? 'border-red-500 bg-red-50' 
                      : focusedField === 'email' 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="Enter your email address"
                  required
                  aria-invalid={!!fieldErrors.email}
                  aria-describedby={fieldErrors.email ? "email-error" : undefined}
                />
                {fieldErrors.email && (
                  <p id="email-error" className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{fieldErrors.email}</span>
                  </p>
                )}
              </div>
              
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  onFocus={() => setFocusedField('message')}
                  onBlur={() => setFocusedField(null)}
                  rows={5}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none ${
                    fieldErrors.message 
                      ? 'border-red-500 bg-red-50' 
                      : focusedField === 'message' 
                      ? 'border-primary-500 bg-primary-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                  placeholder="Tell us how we can help you..."
                  required
                  aria-invalid={!!fieldErrors.message}
                  aria-describedby={fieldErrors.message ? "message-error" : undefined}
                />
                {fieldErrors.message && (
                  <p id="message-error" className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                    <AlertCircle className="h-4 w-4" />
                    <span>{fieldErrors.message}</span>
                  </p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className={`w-full flex items-center justify-center space-x-2 px-6 py-3 rounded-lg font-semibold text-white transition-all duration-200 ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-primary-600 to-purple-600 hover:from-primary-700 hover:to-purple-700 transform hover:scale-105 lg:hover:scale-105 active:scale-95'
                }`}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
            
            <div aria-live="polite" className="sr-only">
              {error || success}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}